import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";

import { TABLE_RECORD_PER_PAGE } from "../../../constants";
import { apiEndpoints, httpMethods } from "../../../constants/api.constants";
import {
  durationChoiceFields,
  durationColumns,
  loaderPath,
} from "../../../descriptions/settings.descriptions";
import useAPI from "../../../hooks/useAPI";
import useDebounce from "../../../hooks/useDebounce";
import useI18N from "../../../hooks/useI18N";
import usePagination from "../../../hooks/usePagination";
import { addMusharkaDuration } from "../../../redux/slices/settings.slices";
import { addRowsPerPage } from "../../../redux/slices/table.slices";
import { Edit, Search } from "../../../shared/icon";
import MUIModal from "../../../shared/MUIModal";
import { MUIStyled } from "../../../shared/MUIStyled";
import SABasicTable from "../../../shared/SABasicTable";
import SABox from "../../../shared/SABox";
import SAButton from "../../../shared/SAButton";
import SACard from "../../../shared/SACard";
import SAForm from "../../../shared/SAForm";
import SAFormFields from "../../../shared/SAFormFields";
import SAStack from "../../../shared/SAStack";
import SAStatusBadge from "../../../shared/SAStatusBadge";
import SATextField from "../../../shared/SATextField";
import SATypography from "../../../shared/SATypography";

const SearchWrapper = MUIStyled(SAStack)({
  maxWidth: 400,
  width: "100%",
});

const MusharkaDuration = () => {
  const { t } = useI18N();
  const { api } = useAPI();
  const dispatch = useDispatch();

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("add");
  const [modalDefaultValues, setModalDefaultValues] = useState({});
  const [loading, setLoading] = useState(false);
  const [rowLoading, setRowLoading] = useState(null); // for enable/disable button
  const [riskLevelOptions, setRiskLevelOptions] = useState([]);
  const [riskLevelLoading, setRiskLevelLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const { register, handleSubmit, reset, control, formState } = useForm({
    defaultValues: modalDefaultValues,
    mode: "onChange",
  });
  const { isDirty } = formState;

  const musharkaDurationList = useSelector(
    (state) => state.settings?.musharkaDuration,
  );
  const { count, results: musharkaDuration } = musharkaDurationList || {};

  const tableRows = useSelector(
    (state) => state?.rowsPerPage?.musharakaDuration,
  );

  const debouncedSearch = useDebounce(searchTerm, 300, () => setCurrentPage(1));

  const {
    totalPage,
    currentPage,
    itemPerPage,
    setCurrentPage,
    setItemPerPage,
  } = usePagination(count, tableRows || TABLE_RECORD_PER_PAGE);

  const handleSetItemPerPage = (val) => {
    setItemPerPage(val);
    dispatch(addRowsPerPage({ name: "musharakaDuration", data: val }));
  };

  useEffect(() => {
    fetchDurations();
  }, [currentPage, itemPerPage, debouncedSearch]);

  useEffect(() => {
    if (modalOpen) {
      fetchRiskLevel();
      reset(modalDefaultValues || {});
    }
  }, [modalOpen, modalDefaultValues, reset]);

  const handleSearchChange = (e) => setSearchTerm(e.target.value);

  const fetchDurations = async () => {
    setLoading(true);
    const response = await api({
      method: httpMethods.get,
      endPoint: apiEndpoints.musharkaDurationChoices,
      loader: loaderPath.getMusharkaDuration,
      needLoader: true,
      params: {
        page: currentPage,
        page_size: itemPerPage,
        name: debouncedSearch,
      },
      showToastOnError: true,
    });
    if (response?.status) {
      dispatch(addMusharkaDuration({ data: response?.data }));
    }
    setLoading(false);
  };

  const handleStatusChange = async (data) => {
    if (!data?.id) return;
    setRowLoading(data?.id);
    const response = await api({
      method: httpMethods.patch,
      endPoint: `${apiEndpoints.musharkaDurationChoices}${data.id}/`,
      data: { is_active: !data.is_active },
      showToastMessage: true,
    });
    if (response?.status) fetchDurations();
    setRowLoading(null);
  };

  const openAddModal = () => {
    setModalMode("add");
    setModalDefaultValues({ is_active: true });
    setModalOpen(true);
  };

  const openEditModal = (data) => {
    let number = "";
    let durationName = "";
    if (typeof data.name === "string") {
      const parts = data.name.split(" ");
      if (parts.length >= 2) {
        number = parts[0];
        durationName = parts.slice(1).join(" ").toLowerCase();

        if (durationName === "weeks") durationName = "week";
        if (durationName === "months") durationName = "month";
        if (durationName === "years") durationName = "year";
      }
    }

    const risk_level = Array.isArray(data.risk_levels)
      ? data.risk_levels.map((rl) => rl.id)
      : [];
    setModalMode("edit");
    setModalDefaultValues({
      ...data,
      number: number || data.number || "",
      name: durationName || data.name?.toLowerCase() || "",
      risk_level,
    });
    setModalOpen(true);
  };

  const getDays = (name, number) => {
    if (name === "week") return Number(number) * 7;
    if (name === "month") return Number(number) * 30;
    if (name === "year") return Number(number) * 365;
    return 0;
  };

  const fetchRiskLevel = async () => {
    setRiskLevelLoading(true);
    const response = await api({
      method: httpMethods.get,
      endPoint: apiEndpoints.riskLevel,
      needLoader: true,
      loader: loaderPath.addRiskLevel,
      showToastOnError: true,
    });
    if (response?.status && Array.isArray(response?.data?.results)) {
      setRiskLevelOptions(
        response.data.results.map((item) => ({
          label: item?.risk_level?.toLowerCase(),
          value: item?.id,
        })),
      );
    }
    setRiskLevelLoading(false);
  };

  const onSubmit = async (formData) => {
    const { name, number, is_active, id, risk_level } = formData;
    const days = getDays(name, number);
    let durationLabel =
      name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
    if (Number(number) > 1) {
      if (durationLabel === "Week") durationLabel = "Weeks";
      if (durationLabel === "Month") durationLabel = "Months";
      if (durationLabel === "Year") durationLabel = "Years";
    }
    const apiData = {
      name: `${number} ${durationLabel}`,
      days,
      is_active: Boolean(is_active),
      risk_level_ids: Array.isArray(risk_level) ? risk_level : [],
    };

    const isEdit = Boolean(id);
    const response = await api({
      method: httpMethods[isEdit ? "patch" : "post"],
      endPoint: isEdit
        ? `${apiEndpoints.musharkaDurationChoices}${id}/`
        : apiEndpoints.musharkaDurationChoices,
      data: apiData,
      loader: loaderPath.addMusharkaDuration,
      needLoader: true,
      showToastMessage: true,
    });
    if (response?.status) {
      fetchDurations();
      setModalOpen(false);
    }
  };

  const paginationProps = {
    currentPage,
    setItemPerPage: handleSetItemPerPage,
    setCurrentPage,
    page: totalPage,
    rowsPerPage: itemPerPage,
  };

  const renderCell = (columnKey, data) => {
    // console.log("data", data);
    switch (columnKey) {
      case "name":
        return <SATypography variant="body2">{data?.name}</SATypography>;

      case "is_active":
        return (
          <SAStatusBadge status={data?.is_active ? "ENABLED" : "DISABLED"} />
        );

      case "risk_level_assign":
        return (
          <SATypography variant="body2">
            {Array.isArray(data?.risk_levels) && data?.risk_levels?.length > 0
              ? data.risk_levels
                  .slice()
                  .sort((a, b) => {
                    const order = { low: 3, medium: 2, high: 1 };
                    return (
                      order[a.risk_level?.toLowerCase()] -
                      order[b.risk_level?.toLowerCase()]
                    );
                  })
                  .map((rl) => t(rl.risk_level?.toLowerCase()))
                  .join(", ")
              : "-"}
          </SATypography>
        );

      case "action":
        return (
          <SAButton
            size="small"
            variant="outlined"
            onClick={() => handleStatusChange(data)}
            loading={rowLoading === data?.id}
          >
            {t(data?.is_active ? "disable" : "enable")}
          </SAButton>
        );

      case "edit":
        return (
          <SAButton
            size="small"
            variant="outlined"
            onClick={() => openEditModal(data)}
            sx={{ "& svg": { ml: 1 } }}
          >
            {t("edit-button")}
            <Edit />
          </SAButton>
        );

      default:
        return data[columnKey];
    }
  };

  return (
    <>
      <SACard>
        <SAStack
          pb={2}
          mb={1}
          alignItems="center"
          direction="row"
          gap={2}
          justifyContent="space-between"
          borderBottom="1px solid gray.main 50"
        >
          <SearchWrapper>
            <SATextField
              value={searchTerm}
              onChange={handleSearchChange}
              placeholder={t("search-for-durations")}
              InputProps={{ startAdornment: <Search /> }}
            />
          </SearchWrapper>
          <SAButton color="primary" variant="contained" onClick={openAddModal}>
            {t("add-musharka-duration")}
          </SAButton>
        </SAStack>
        <SABasicTable
          {...{
            renderCell,
            paginationProps,
            data: musharkaDuration,
            loading,
            columns: durationColumns,
          }}
        />
      </SACard>

      <MUIModal
        open={modalOpen}
        maxWidth="xs"
        handleClose={() => setModalOpen(false)}
        title={t(
          modalMode === "add"
            ? "add-musharka-duration-title"
            : "edit-musharka-duration-title",
        )}
        content={
          <SABox
            py={4}
            px={{ xs: 2, md: 4 }}
            sx={{ backgroundColor: "#fff", borderRadius: 3, boxShadow: 1 }}
          >
            <SAForm
              key={modalDefaultValues?.id || modalMode}
              onSubmit={handleSubmit(onSubmit)}
            >
              <SAFormFields
                spacing={3}
                fields={durationChoiceFields.map((field) =>
                  field.name === "risk_level"
                    ? {
                        ...field,
                        options: riskLevelOptions,
                        loading: riskLevelLoading,
                      }
                    : field,
                )}
                register={register}
                formState={formState}
                control={control}
                defaultValues={modalDefaultValues}
              />
              <SABox mt={4} textAlign="center">
                {modalMode === "add" ? (
                  <SAButton
                    type="submit"
                    color="primary"
                    variant="contained"
                    loading={loading}
                  >
                    {t("add-musharka-duration")}
                  </SAButton>
                ) : (
                  <SAButton
                    type="submit"
                    disabled={!isDirty}
                    color="primary"
                    variant="contained"
                    loading={loading}
                  >
                    {t("save-changes")}
                  </SAButton>
                )}
              </SABox>
            </SAForm>
          </SABox>
        }
      />
    </>
  );
};

export default MusharkaDuration;
